import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';
import NoiRockLogo from '../../../components/NoiRockLogo';

/**
 * Distinct visual identity for the super admin shell — purple accent
 * (vs the brand blue used in tenant-facing surfaces) so it's instantly
 * recognizable as system-level.
 */
const NAV = [
  { to: '/superadmin',                 label: 'Overview',       icon: '◆' },
  { to: '/superadmin/tenants',         label: 'Tenants',        icon: '◧' },
  { to: '/superadmin/business-rules',  label: 'Business rules', icon: '⚙' },
];

const SuperAdminLayout = ({ children, title, subtitle, actions }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || cancelled) return;
      const { data } = await supabase
        .from('profiles')
        .select('full_name, language')
        .eq('id', user.id)
        .single();
      if (!cancelled) setProfile(data);
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/login-authentication', { replace: true });
  };

  const isActive = (path) =>
    path === '/superadmin' ? location.pathname === '/superadmin' : location.pathname.startsWith(path);

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar — accent purple on left, lock visual to system level */}
      <aside className="hidden md:flex flex-col w-60 shrink-0 border-r border-border bg-card">
        <div className="px-5 py-5 border-b border-border flex items-center gap-3">
          <NoiRockLogo size={32} />
          <div className="leading-tight">
            <div className="font-heading font-bold text-foreground text-sm">NoiRock</div>
            <div className="text-[10px] uppercase tracking-widest text-accent font-semibold">
              {t('superadmin.system', 'System')}
            </div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {NAV.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive(item.to)
                  ? 'bg-accent/10 text-accent'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              <span className="text-base">{item.icon}</span>
              <span>{t(`superadmin.nav.${item.label.toLowerCase()}`, item.label)}</span>
            </Link>
          ))}
        </nav>

        <div className="p-3 border-t border-border">
          <div className="px-3 py-2 mb-2">
            <div className="text-xs text-muted-foreground">{t('superadmin.signedInAs', 'Signed in as')}</div>
            <div className="text-sm font-medium text-foreground truncate">
              {profile?.full_name || '—'}
            </div>
          </div>
          <button
            onClick={handleSignOut}
            className="w-full px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted hover:text-error transition-colors text-left"
          >
            {t('superadmin.signOut', 'Sign out')}
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 flex flex-col">
        {/* Top bar (mobile shows logo + sign out) */}
        <header className="md:hidden sticky top-0 z-20 bg-card border-b border-border px-4 py-3 flex items-center justify-between">
          <NoiRockLogo size={26} withText />
          <button
            onClick={handleSignOut}
            className="text-xs text-muted-foreground hover:text-error transition-colors"
          >
            {t('superadmin.signOut', 'Sign out')}
          </button>
        </header>

        {/* Page heading */}
        <div className="px-5 md:px-8 py-6 border-b border-border bg-card">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row md:items-end md:justify-between gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-accent font-semibold mb-1">
                {t('superadmin.systemConsole', 'System console')}
              </div>
              {title && <h1 className="font-heading text-2xl font-bold text-foreground">{title}</h1>}
              {subtitle && <p className="text-muted-foreground text-sm mt-1">{subtitle}</p>}
            </div>
            {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 px-5 md:px-8 py-6">
          <div className="max-w-6xl mx-auto">{children}</div>
        </div>

        {/* Mobile bottom nav */}
        <nav className="md:hidden sticky bottom-0 z-20 bg-card border-t border-border flex">
          {NAV.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`flex-1 flex flex-col items-center gap-0.5 py-3 text-xs font-medium transition-colors ${
                isActive(item.to) ? 'text-accent' : 'text-muted-foreground'
              }`}
            >
              <span className="text-lg">{item.icon}</span>
              <span>{t(`superadmin.nav.${item.label.toLowerCase()}`, item.label)}</span>
            </Link>
          ))}
        </nav>
      </main>
    </div>
  );
};

export default SuperAdminLayout;
